'use client';

import { Grid, Typography } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import React, { FC, useMemo } from 'react';
import { MethodOfAcceptingTermsAndConditions } from '../../enums';
import { SetShowDialog } from '../../types';
import { dayjs } from '../../utils';
import { CenteredDismissModal } from '../modals';

type TermsUser = {
	firstName?: string;
	lastName?: string;
	email?: string;
} | null;

type TermsAcceptanceRow = {
	acceptanceDate?: string;
	methodOfAcceptance?: string;
	user?: TermsUser;
	termsAndConditionsHistory?: {
		id?: string;
		summary?: string;
		text?: string;
	};
};

type TermsAndConditionsProps = {
	subscriptionTermsAndConditionsAcceptance?: TermsAcceptanceRow[];
	hasAcceptedMostRecentTerms?: boolean;
	setShowDialog?: SetShowDialog;
	timezone: string;
};

export const TermsAndConditions: FC<TermsAndConditionsProps> = ({
	subscriptionTermsAndConditionsAcceptance = [],
	hasAcceptedMostRecentTerms,
	setShowDialog,
	timezone,
}) => {
	const rows = useMemo(() => {
		let currentVersion = 0;
		let lastHistoryId: string | null = null;

		const temp = subscriptionTermsAndConditionsAcceptance.map((item, index) => {
			const historyId = item.termsAndConditionsHistory?.id ?? null;

			if (historyId !== lastHistoryId) {
				currentVersion += 1;
				lastHistoryId = historyId;
			}

			return {
				id: `${item.acceptanceDate || 'acceptance'}-${index}`,
				...item,
				versionGroup: currentVersion,
			};
		});

		const maxVersion = currentVersion;

		return temp.map((row) => ({
			...row,
			version: maxVersion - row.versionGroup + 1,
		}));
	}, [subscriptionTermsAndConditionsAcceptance]);

	const latestAcceptance = rows.length ? rows[0] : null;

	const { user = {}, acceptanceDate, methodOfAcceptance, version } = latestAcceptance ?? {};

	const { firstName = '', lastName = '', email = '' } = user ?? {};

	const acceptedBy = firstName ? `${firstName} ${lastName} - ${email}` : 'N/A';

	const formattedAcceptanceDate = (() => {
		if (!acceptanceDate) return 'N/A';

		const parsed = dayjs.utc(acceptanceDate).tz(timezone);

		return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
	})();

	const columns: GridColDef[] = [
		{
			field: 'version',
			headerName: 'Version',
			flex: 1,
			minWidth: 120,
		},
		{
			field: 'acceptanceDate',
			headerName: 'Acceptance Date',
			flex: 1.2,
			minWidth: 180,
			valueGetter: (params) => {
				const date = params.row?.acceptanceDate;
				if (!date) return 'N/A';

				const parsed = dayjs.utc(date).tz(timezone);
				return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A z') : 'N/A';
			},
		},
		{
			field: 'methodOfAcceptance',
			headerName: 'Method of Acceptance',
			flex: 1.2,
			minWidth: 180,
			valueGetter: (params) => MethodOfAcceptingTermsAndConditions[params.row?.methodOfAcceptance] || 'N/A',
		},
		{
			field: 'acceptedBy',
			headerName: 'Accepted By',
			flex: 1.2,
			minWidth: 180,
			valueGetter: (params) => {
				const first = params.row?.user?.firstName || '';
				const last = params.row?.user?.lastName || '';
				const fullName = `${first} ${last}`.trim();
				return fullName || 'N/A';
			},
		},
		{
			field: 'userIp',
			headerName: 'IP Address',
			flex: 1.4,
			minWidth: 220,
			valueGetter: (params) => params.row?.userIp || 'N/A',
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1.4,
			minWidth: 220,
			valueGetter: (params) => params.row?.user?.email || 'N/A',
		},
		{
			field: 'termsAndConditions',
			headerName: 'Terms & Conditions',
			sortable: false,
			filterable: false,
			width: 175,
			renderCell: (params) => (
				<span
					style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
					onClick={() =>
						setShowDialog(
							<CenteredDismissModal
								title='Terms & Conditions Details'
								content={
									<div style={{ maxHeight: '300px', overflowY: 'auto' }}>
										<p>
											<strong>Summary:</strong>{' '}
											{params.row?.termsAndConditionsHistory?.summary || 'N/A'}
										</p>
										<p>
											<strong>Full Text:</strong>{' '}
											{params.row?.termsAndConditionsHistory?.text || 'N/A'}
										</p>
									</div>
								}
								dismissText='Close'
								handleDismiss={() => setShowDialog(null)}
								width='500px'
								titleStyle={{ fontWeight: 'bold', textAlign: 'center' }}
							/>,
						)
					}
				>
					View
				</span>
			),
		},
	];

	return (
		<Grid container direction='column' gap={3}>
			<Grid container direction='row'>
				<Grid
					className='subscriptionInfoGrid'
					container
					sx={{
						flexDirection: 'column',
						marginLeft: '0px',
						width: '30%',
						minWidth: '350px',
						p: 2,
						borderRadius: 2,
						backgroundColor: 'grey.100',
					}}
				>
					<Typography variant='h6' gutterBottom marginBottom='15px'>
						Terms & Conditions Acceptance
					</Typography>

					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted on <strong>{formattedAcceptanceDate}</strong>
						</span>
					</Grid>

					<Grid item>
						<span style={{ color: '#555' }}>
							Accepted By: <strong>{acceptedBy}</strong>
						</span>
					</Grid>

					<Grid item>
						<span style={{ color: '#555' }}>
							Method of Acceptance:{' '}
							<strong>{MethodOfAcceptingTermsAndConditions[methodOfAcceptance] || 'N/A'}</strong>
						</span>
					</Grid>

					{latestAcceptance && (
						<Grid item>
							<span style={{ color: '#555' }}>
								Version: <strong>{version}</strong>
							</span>
						</Grid>
					)}

					{latestAcceptance && (
						<Grid item>
							<span style={{ color: '#555' }}>
								Has accepted most recent terms:{' '}
								<strong>{hasAcceptedMostRecentTerms ? 'Yes' : 'No'}</strong>
							</span>
						</Grid>
					)}
				</Grid>
			</Grid>

			{rows.length > 0 && (
				<div data-cy='terms-and-conditions-history-table-container'>
					<Typography variant='h6' gutterBottom>
						Acceptance History
					</Typography>

					<DataGridPremium
						autoHeight
						rows={rows}
						columns={columns}
						disableRowSelectionOnClick
						hideFooter
						getRowId={(row) => row.id}
						sx={{ backgroundColor: 'white' }}
					/>
				</div>
			)}
		</Grid>
	);
};
